(() => {
  const ELEMENT_NAME = 'luma-product-experience';
  const STORAGE_VERSION = 1;
  const DEFAULT_STORAGE_KEY = `luma:selector:all-products:v${STORAGE_VERSION}`;
  const DEFAULT_PACK_READY_EVENT = 'luma:pack-ready';

  class LumaProductExperience extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.storageKey = this.dataset.storageKey || DEFAULT_STORAGE_KEY;
      this.offerQuantity = 2;
      this.selected = [];

      this.offers = [...this.querySelectorAll('[data-lpx-offer]')];
      this.offerSection = this.querySelector('[data-lpx-offers]');
      this.count = this.querySelector('[data-lpx-count]');
      this.total = this.querySelector('[data-lpx-total]');
      this.continueButton = this.querySelector('[data-lpx-continue]');
      this.fallbackLink = this.querySelector('[data-lpx-fallback]');
      this.designStatus = this.querySelector('[data-lpx-design-status]');

      this.currentProduct = this.validProduct({
        productId: this.dataset.productId || '',
        variantId: this.dataset.variantId || '',
        title: this.dataset.productTitle || '',
        handle: this.dataset.productHandle || '',
        imageUrl: this.dataset.imageUrl || '',
        productUrl: this.dataset.productUrl || '',
      });

      this.restoreState();
      this.ensureCurrentProductFirst();
      this.persist();

      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    safeQuantity(value) {
      const quantity = Number(value);
      return [1, 2, 3].includes(quantity) ? quantity : null;
    }

    priceForQuantity(quantity) {
      const offer = this.offers.find(
        (item) => Number(item.dataset.quantity) === quantity,
      );
      const price = Number(offer?.dataset.price);
      return Number.isFinite(price) && price > 0 ? price : 0;
    }

    validProduct(value) {
      if (!value || typeof value !== 'object') return null;
      const required = [
        'productId',
        'variantId',
        'title',
        'handle',
        'imageUrl',
        'productUrl',
      ];
      if (required.some((key) => typeof value[key] !== 'string')) return null;
      if (!value.productId || !value.variantId || !value.title || !value.handle) {
        return null;
      }
      return {
        productId: value.productId,
        variantId: value.variantId,
        title: value.title.slice(0, 240),
        handle: value.handle.slice(0, 180),
        imageUrl: value.imageUrl,
        productUrl: value.productUrl,
      };
    }

    restoreState() {
      let stored;
      try {
        stored = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
      } catch {
        try {
          sessionStorage.removeItem(this.storageKey);
        } catch {
          // The experience remains usable without persistent state.
        }
        return;
      }

      if (!stored || stored.version !== STORAGE_VERSION) return;

      const quantity = this.safeQuantity(stored.offerQuantity);
      if (quantity) this.offerQuantity = quantity;

      const unique = new Set();
      this.selected = Array.isArray(stored.selectedProducts)
        ? stored.selectedProducts
            .map((product) => this.validProduct(product))
            .filter(
              (product) =>
                product &&
                !unique.has(product.productId) &&
                unique.add(product.productId),
            )
            .slice(0, this.offerQuantity)
        : [];
    }

    ensureCurrentProductFirst() {
      if (!this.currentProduct) return;
      this.selected = [
        this.currentProduct,
        ...this.selected.filter(
          (product) => product.productId !== this.currentProduct.productId,
        ),
      ].slice(0, this.offerQuantity);
    }

    persist() {
      const state = {
        version: STORAGE_VERSION,
        offerQuantity: this.offerQuantity,
        selectedProducts: this.selected,
      };
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(state));
      } catch {
        // The experience remains usable without persistent state.
      }
      return state;
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-lpx-scroll-offers]')) {
        this.offerSection?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'start',
        });
        this.offerSection?.focus({ preventScroll: true });
        return;
      }

      const offer = target.closest('[data-lpx-offer]');
      if (offer && this.contains(offer)) {
        const quantity = this.safeQuantity(offer.dataset.quantity);
        if (quantity) this.changeOffer(quantity);
        return;
      }

      if (target.closest('[data-lpx-continue]')) {
        this.continue();
      }
    }

    changeOffer(quantity) {
      this.offerQuantity = quantity;
      this.ensureCurrentProductFirst();
      this.persist();
      this.render();
    }

    ctaLabel() {
      if (this.offerQuantity === 1) return 'هاد العطر هو اختياري';
      if (this.offerQuantity === 2) return 'زيد عطر ثاني';
      return 'كمّل Collection ديالك';
    }

    builderUrl() {
      const fallback = new URL(
        '/collections/all-products',
        window.location.origin,
      );
      try {
        const configured = new URL(
          this.dataset.builderUrl || fallback.pathname + fallback.search,
          window.location.origin,
        );
        return configured.origin === window.location.origin
          ? configured
          : fallback;
      } catch {
        return fallback;
      }
    }

    getPublicState() {
      const selectedProducts = this.selected.map((product) => ({ ...product }));
      return {
        offerQuantity: this.offerQuantity,
        totalPrice: this.priceForQuantity(this.offerQuantity),
        selectedProducts,
        productIds: selectedProducts.map((product) => product.productId),
        variantIds: selectedProducts.map((product) => product.variantId),
        productTitles: selectedProducts.map((product) => product.title),
        imageUrls: selectedProducts.map((product) => product.imageUrl),
        sourcePageUrl: window.location.href,
      };
    }

    emitPackReady() {
      window.dispatchEvent(
        new CustomEvent(
          this.dataset.packReadyEvent || DEFAULT_PACK_READY_EVENT,
          { detail: this.getPublicState() },
        ),
      );
    }

    continue() {
      this.ensureCurrentProductFirst();
      this.persist();

      if (this.offerQuantity === 1) {
        this.emitPackReady();
        if (this.designStatus) this.designStatus.hidden = false;
        return;
      }

      window.location.assign(this.builderUrl());
    }

    render() {
      this.offers.forEach((offer) => {
        const selected =
          Number(offer.dataset.quantity) === this.offerQuantity;
        offer.classList.toggle('is-selected', selected);
        offer.setAttribute('aria-pressed', String(selected));
        offer.setAttribute('aria-checked', String(selected));
      });

      const price = this.priceForQuantity(this.offerQuantity);
      if (this.count) {
        this.count.textContent = `1/${this.offerQuantity}`;
      }
      if (this.total) {
        this.total.textContent = `${price} درهم`;
      }
      if (this.continueButton) {
        this.continueButton.textContent = this.ctaLabel();
      }
      if (this.fallbackLink) {
        this.fallbackLink.hidden = this.offerQuantity !== 1;
        this.fallbackLink.href = this.builderUrl().toString();
      }
      if (this.designStatus && this.offerQuantity !== 1) {
        this.designStatus.hidden = true;
      }
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, LumaProductExperience);
  }
})();
